import { useState, useMemo } from 'react'

import { encode } from 'js-base64'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import { env } from '@/env'

import { useAvatarContext, AvatarProvider } from './context'
import { AvatarEditor } from './Editor'
import { AvatarBeard as Beard } from './Pieces/Beard'
import { AvatarBody as Body } from './Pieces/Body'
import { AvatarBottom as Bottom } from './Pieces/Bottom'
import { AvatarEyes as Eyes } from './Pieces/Eyes'
import { AvatarFooter as Footer } from './Pieces/Footer'
import { AvatarHair as Hair } from './Pieces/Hair'
import { AvatarHead as Head } from './Pieces/Head'
import { AvatarMouth as Mouth } from './Pieces/Mouth'
import { AvatarNose as Nose } from './Pieces/Nose'
import { AvatarShirt as Shirt } from './Pieces/Shirt'
import styles from './styles.module.scss'
import { getRandomConfig, defaultOptions, configHasChanges } from './utils'

const ENABLE_FULL_BODY = false
const isDev = env.NODE_ENV == 'development'
const getConfigId = config => {
  const object = { ...config }

  const keys = Object.keys(object)
  const values = Object.values(object)
  const compressedKeys = keys.map(key => key[0])

  let compressedObject = _.zipObject(compressedKeys, values)

  try {
    compressedObject = JSON.stringify(compressedObject)
    compressedObject = encode(compressedObject)
  } catch (error) {
    compressedObject = uuidv4()
  }

  return compressedObject
}

export const Avatar = ({
  showFullBody: showFullBodyProps,
  className,
  config: userConfig = {}
}) => {
  // Gera config random só na montagem
  const [initialConfig] = useState(() => getRandomConfig())

  // Faz merge de forma memoizada
  const config = useMemo(
    () => _.merge(_.cloneDeep(initialConfig), userConfig),
    [initialConfig, userConfig]
  )

  // Gera id baseado na config final
  const id = useMemo(() => getConfigId(config), [config])

  const showFullBody = ENABLE_FULL_BODY && showFullBodyProps

  const canvaSvgAttrs = useMemo(
    () => ({
      width: '150',
      height: '460',
      viewBox: '0 0 150 460'
    }),
    []
  )

  return (
    <div className="aspect-square">
      <div className="w-full h-full">
        <div
          id={id}
          className={className}
          style={{
            background: config.bgColor,
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            height: '100%'
          }}
        >
          <svg
            {...canvaSvgAttrs}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              width: '100%',
              height: showFullBody ? '100%' : 'auto',
              pointerEvents: isDev ? 'none' : 'none',
              transition: 'all .3s ease-in-out'
            }}
            className={styles.Avatar}
          >
            <animate
              attributeName="viewBox"
              to={canvaSvgAttrs.viewBox}
              dur="5s"
              fill="freeze"
            />
            <Hair.Back {...config.hair} />
            <Body id={id} {...config.body} />
            <Head id={id} {...config.head} />
            <Hair {...config.hair} />
            <Eyes {...config.eyes} />
            <Nose {...config.nose} />
            <Mouth {...config.mouth} />
            <Beard {...config.beard} />
            <Shirt {...config.shirt} />
            {ENABLE_FULL_BODY && (
              <>
                <Bottom {...config.bottom} />
                <Footer {...config.footer} />
              </>
            )}
          </svg>
        </div>
      </div>
    </div>
  )
}

Avatar.Editor = AvatarEditor

export {
  getRandomConfig,
  defaultOptions,
  configHasChanges,
  useAvatarContext,
  AvatarProvider
}
